import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { Database } from '../types/database'
import toast from 'react-hot-toast'
import { activateTrialWithRetry, generateDeviceFingerprint } from '../services/trialService'
import { trackFunnelEvent } from '../lib/funnelEvents'
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat'

type Profile = Database['public']['Tables']['profiles']['Row']

/** Como o usuario saiu do onboarding sem concluir. */
export type OnboardingDismissVia = 'skip' | 'close' | 'backdrop'

interface AuthContextType {
  user: User | null
  profile: Profile | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
  isSchoolAdmin: boolean
  isEditor: boolean
  /**
   * `activate-trial` em andamento para o usuário atual. Um cadastro recém-feito
   * ainda não tem linha em `subscriptions`; sem este sinal o TrialGuard lia
   * "sem assinatura" e mandava para /app/billing o professor que acabava de
   * ganhar 7 dias — e o onboarding (que só vive no Dashboard) nunca abria.
   */
  trialActivating: boolean
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>
  signUp: (email: string, password: string, userData: { nome: string; escola?: string; captchaToken?: string }) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<void>
  refreshProfile: () => Promise<Profile | null>
  markOnboardingComplete: () => Promise<void>
  markOnboardingDismissed: (step: number, via: OnboardingDismissVia) => Promise<void>
}

// Configuration constants
const AUTH_TIMEOUT = 10000 // 10 seconds maximum for auth initialization
const PROFILE_TIMEOUT = 10000 // 10 seconds maximum for profile fetching (increased from 5s)
const MAX_PROFILE_RETRIES = 2 // Reduced from 3 to 2 for faster fallback
// Teto da espera pelo auto-trial. Mesmo valor do ConversionModal. Estourado, o
// guard volta ao comportamento antigo (billing) em vez de girar para sempre.
export const TRIAL_ACTIVATION_TIMEOUT_MS = 10_000

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [trialActivating, setTrialActivating] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [initializing, setInitializing] = useState(false)

  // Refs for timeout management
  const authTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const profileTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)
  const trialAttemptedRef = useRef<Set<string>>(new Set())
  const acquisitionAttemptedRef = useRef<Set<string>>(new Set())

  const ensureTrialActive = async (userId: string) => {
    if (trialAttemptedRef.current.has(userId)) return
    trialAttemptedRef.current.add(userId)

    // Liga ANTES do primeiro await: o SubscriptionContext busca a assinatura no
    // mesmo commit, e se terminasse antes deste flag o guard já redirecionaria.
    setTrialActivating(true)
    const teto = setTimeout(() => {
      if (mountedRef.current) setTrialActivating(false)
    }, TRIAL_ACTIVATION_TIMEOUT_MS)

    try {
      // Qualquer linha conta, não só active/trialing: quem teve o trial expirado
      // fica com status 'canceled' e voltaria a pedir ativação a cada carregamento
      // só para receber um 403 previsível. A subscription é o sinal mais confiável
      // que trial_history porque `activate-trial` insere a subscription antes de
      // gravar o histórico — e tolera falha no histórico.
      const { data: existing } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle()

      if (existing) return

      const result = await activateTrialWithRetry({
        planType: 'premium',
        deviceFingerprint: generateDeviceFingerprint(),
      })

      if (result.success) {
        // Síncrono: o listener do SubscriptionContext entra em loading neste
        // mesmo tick, e o React agrupa isso com o `setTrialActivating(false)`
        // do finally — o guard não vê um render intermediário "sem assinatura".
        window.dispatchEvent(new CustomEvent('trial-activated'))
      }
    } catch {
      trialAttemptedRef.current.delete(userId)
    } finally {
      clearTimeout(teto)
      if (mountedRef.current) setTrialActivating(false)
    }
  }

  // Utility function to create a timeout promise
  const createTimeoutPromise = (ms: number, errorMessage: string) => {
    return new Promise<never>((_, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(errorMessage))
      }, ms)

      // Store timeout ID for cleanup
      if (errorMessage.includes('profile')) {
        profileTimeoutRef.current = timeoutId
      }
    })
  }

  const fetchProfile = async (userId: string, retryCount = 0): Promise<Profile | null> => {
    try {
      // Create the profile fetch promise with timeout
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      const timeoutPromise = createTimeoutPromise(
        PROFILE_TIMEOUT,
        `Profile fetch timeout after ${PROFILE_TIMEOUT}ms`
      )

      // Race between profile fetch and timeout
      const { data: profileData, error: profileError } = await Promise.race([
        profilePromise,
        timeoutPromise
      ])

      // Clear timeout if successful
      if (profileTimeoutRef.current) {
        clearTimeout(profileTimeoutRef.current)
        profileTimeoutRef.current = null
      }

      if (profileError) {
        // If profile not found and not the last attempt, retry with shorter delay
        if (retryCount < MAX_PROFILE_RETRIES) {
          if (!mountedRef.current) return null
          await new Promise(resolve => setTimeout(resolve, 1000))
          if (!mountedRef.current) return null
          return fetchProfile(userId, retryCount + 1)
        }

        return null // Return null instead of throwing to allow app to continue
      }

      return profileData
    } catch (error) {
      // Clear timeout on error
      if (profileTimeoutRef.current) {
        clearTimeout(profileTimeoutRef.current)
        profileTimeoutRef.current = null
      }

      // If it's a timeout error and we haven't exceeded retries, try again
      if (error instanceof Error && error.message.includes('timeout') && retryCount < MAX_PROFILE_RETRIES) {
        if (!mountedRef.current) return null
        return fetchProfile(userId, retryCount + 1)
      }

      return null // Return null to allow app to continue
    }
  }

  const refreshProfile = async (): Promise<Profile | null> => {
    if (!user) {
      return null;
    }

    try {
      const profileData = await fetchProfile(user.id, 0);

      if (profileData) {
        setProfile(profileData);
      }
      return profileData ?? null;
    } catch (error) {
      toast.error('Erro ao atualizar dados do perfil');
      return null;
    }
  };

  // Detect if we're returning from an OAuth callback (PKCE or implicit flow)
  const isOAuthCallback = (): boolean => {
    const params = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    return !!(params.get('code') || hashParams.get('access_token'))
  }

  useEffect(() => {
    let authSubscription: any = null
    mountedRef.current = true

    const initializeAuth = async () => {
      // Prevent multiple initialization attempts
      if (initializing || initialized) {
        return
      }

      setInitializing(true)
      const isCallback = isOAuthCallback()

      try {
        // Set up auth timeout - force completion after AUTH_TIMEOUT
        authTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            setLoading(false)
            setInitialized(true)
            setInitializing(false)
          }
        }, AUTH_TIMEOUT)

        // STEP 1: Register auth state listener FIRST (Supabase best practice)
        // This ensures we catch INITIAL_SESSION and SIGNED_IN events
        // that fire during the internal PKCE code exchange.
        let resolveOAuthWait: ((session: Session | null) => void) | null = null
        let oauthSessionPromise: Promise<Session | null> | null = null

        if (isCallback) {
          oauthSessionPromise = new Promise<Session | null>((resolve) => {
            resolveOAuthWait = resolve
          })
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (!mountedRef.current) return

            // If waiting for OAuth callback, resolve on session-bearing events
            if (resolveOAuthWait && session &&
                (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
              resolveOAuthWait(session)
              resolveOAuthWait = null
            }

            setSession(session)
            setUser(session?.user ?? null)

            if (session?.user) {
              fetchProfile(session.user.id)
                .then(profileData => {
                  if (mountedRef.current) {
                    setProfile(profileData)
                  }
                  // Sync onboarding localStorage cache with database on sign in
                  if (event === 'SIGNED_IN' && profileData) {
                    if (profileData.onboarding_completed) {
                      localStorage.setItem('hasSeenOnboarding', 'true')
                    } else {
                      localStorage.removeItem('hasSeenOnboarding')
                    }
                  }
                })
                .catch(() => {})
            } else {
              setProfile(null)
            }
          }
        )

        authSubscription = subscription

        // STEP 2: Get the current session
        // Give OAuth callbacks more time for the PKCE code exchange (network round-trip)
        const sessionTimeoutMs = isCallback ? 10000 : 5000
        let currentSession: Session | null = null

        try {
          const sessionPromise = supabase.auth.getSession()
          const sessionTimeoutPromise = createTimeoutPromise(
            sessionTimeoutMs,
            `Session fetch timeout after ${sessionTimeoutMs}ms`
          )

          const { data: { session }, error } = await Promise.race([
            sessionPromise,
            sessionTimeoutPromise
          ])

          if (!error) {
            currentSession = session
          }
        } catch {
          // Session fetch timed out or failed
          // If this is an OAuth callback, we'll rely on the listener below
        }

        // STEP 3: If OAuth callback and no session yet, wait for the listener
        if (isCallback && !currentSession && oauthSessionPromise) {
          try {
            const listenerSession = await Promise.race([
              oauthSessionPromise,
              new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
            ])
            if (listenerSession) {
              currentSession = listenerSession
            }
          } catch {
            // Timeout waiting for OAuth session
          }
        }

        // STEP 4: Set session and user state
        if (mountedRef.current) {
          setSession(currentSession)
          setUser(currentSession?.user ?? null)
        }

        // Fetch profile asynchronously if user exists (don't block initialization)
        if (currentSession?.user && mountedRef.current) {
          fetchProfile(currentSession.user.id)
            .then(profileData => {
              if (mountedRef.current) {
                setProfile(profileData)
              }
            })
            .catch(() => {})
        } else if (mountedRef.current) {
          setProfile(null)
        }

        // Clear auth timeout since we completed successfully
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current)
          authTimeoutRef.current = null
        }

        // STEP 5: Complete initialization
        if (mountedRef.current) {
          setLoading(false)
          setInitialized(true)
          setInitializing(false)
        }

      } catch (error) {
        // Clear timeouts on error
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current)
          authTimeoutRef.current = null
        }

        if (mountedRef.current) {
          setLoading(false)
          setInitialized(true)
          setInitializing(false)
        }
      }
    }

    // Initialize auth on mount
    initializeAuth()

    return () => {
      mountedRef.current = false

      // Clear all timeouts
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current)
        authTimeoutRef.current = null
      }
      if (profileTimeoutRef.current) {
        clearTimeout(profileTimeoutRef.current)
        profileTimeoutRef.current = null
      }

      // Unsubscribe from auth changes
      if (authSubscription) {
        authSubscription.unsubscribe()
      }
    }
  }, []) // Empty dependency array to run only once on mount

  // Auto-trial: ensure every authenticated user has an active subscription/trial.
  // Idempotent — activate-trial edge function refuses if one already exists, and the
  // trialAttemptedRef dedupes per-session retries.
  useEffect(() => {
    if (loading || !user?.id) return
    void ensureTrialActive(user.id)
  }, [user?.id, loading])

  // Top-up da atribuicao. Cobre o Google OAuth (que nao passa por `signUp`,
  // logo nao tem `options.data`) e qualquer falha do caminho do metadata.
  //
  // Efeito PROPRIO, de proposito: nao pode viver dentro de `ensureTrialActive`,
  // que retorna cedo em `if (existing) return` quando o usuario ja tem
  // assinatura — seria justamente a base existente ficando sem atribuicao.
  // A RPC e first-write-wins e nao grava linha sem sinal, entao rodar em todo
  // load e seguro.
  useEffect(() => {
    if (loading || !user?.id) return
    if (acquisitionAttemptedRef.current.has(user.id)) return
    acquisitionAttemptedRef.current.add(user.id)

    void (async () => {
      try {
        const { attributionForSignup, deviceSnapshot, localeSnapshot } = await import('../lib/attribution')
        const attribution = attributionForSignup()
        if (!attribution) return

        await supabase.rpc('upsert_user_acquisition', {
          p_payload: {
            ...attribution,
            ...localeSnapshot(),
            device: deviceSnapshot(),
            provider: user.app_metadata?.provider ?? 'email',
            source: 'client_topup'
          } as never
        })
      } catch {
        // atribuicao nunca quebra o login
      }
    })()
  }, [user?.id, user?.app_metadata?.provider, loading])

  // Último uso (profiles.last_active_at) + espelho do último login. Substitui o
  // antigo estatisticas.ultimoAcesso, que só era gravado no SIGNED_IN e perdia
  // quem voltava com a sessão salva.
  useActivityHeartbeat(user?.id)

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      setLoading(true)

      // 🛡️ SECURITY: Verify CAPTCHA token if provided
      if (captchaToken) {
        const { data: captchaResult, error: captchaError } = await supabase.functions.invoke(
          'verify-captcha',
          { body: { token: captchaToken, action: 'login' } }
        )

        if (captchaError || !captchaResult?.success) {
          throw new Error('Verificação de segurança falhou')
        }
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      toast.success('Login realizado com sucesso!')
    } catch (error) {
      const authError = error as AuthError
      toast.error(authError.message || 'Erro ao fazer login')
      throw error
    } finally {
      setLoading(false);
    }
  }

  const signUp = async (email: string, password: string, userData: { nome: string; escola?: string; captchaToken?: string }) => {
    try {
      setLoading(true)

      // 🛡️ SECURITY: Verify CAPTCHA token before signup
      if (userData.captchaToken) {
        const { data: captchaResult, error: captchaError } = await supabase.functions.invoke(
          'verify-captcha',
          { body: { token: userData.captchaToken, action: 'signup' } }
        )

        if (captchaError || !captchaResult?.success) {
          throw new Error('Verificação de segurança falhou')
        }
      }

      // 🛡️ SECURITY: Validate email (block disposable emails)
      const { data: emailValidation, error: emailError } = await supabase.functions.invoke(
        'validate-email',
        { body: { email } }
      )

      if (emailError || !emailValidation?.valid) {
        throw new Error(emailValidation?.error || 'Email inválido ou não permitido')
      }

      // Pass the referral code (if any) through signup metadata so the
      // handle_new_user trigger persists it on the profile row.
      let referrerCode: string | null = null
      try {
        const { consumeReferral } = await import('../lib/referral')
        referrerCode = consumeReferral()
      } catch {
        // referral is optional — silent ignore
      }

      // Atribuicao pelo MESMO caminho do referral, e por um motivo concreto: a
      // confirmacao de e-mail esta ligada, o perfil nasce no trigger de
      // auth.users ANTES de qualquer confirmacao, e existe uma classe de
      // usuarios com linha em profiles e nenhuma sessao jamais criada. Para
      // eles, qualquer gravacao feita "no primeiro load autenticado" nunca roda
      // — e sao justamente os cadastros que o admin precisa investigar.
      // Tambem cobre quem confirma o e-mail em outro navegador, onde o
      // localStorage da visita original nao existe.
      //
      // Aqui apenas LEMOS (nao consumimos): se o trigger falhar, o `EXCEPTION
      // WHEN OTHERS` dele engole o erro em silencio, e o top-up pos-login ainda
      // consegue gravar. A RPC e first-write-wins, entao escrever duas vezes e
      // inofensivo.
      let attributionPayload: Record<string, unknown> | null = null
      try {
        const { readAttribution, deviceSnapshot, localeSnapshot } = await import('../lib/attribution')
        const attribution = readAttribution()
        if (attribution) {
          attributionPayload = {
            ...attribution,
            ...localeSnapshot(),
            device: deviceSnapshot(),
            provider: 'email',
            source: 'signup_metadata',
            ...(referrerCode ? { ref_code: referrerCode } : {})
          }
        }
      } catch {
        // atribuicao e opcional — nunca bloqueia o cadastro
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nome: userData.nome,
            escola: userData.escola,
            ...(referrerCode ? { referrer: referrerCode } : {}),
            ...(attributionPayload ? { attribution: attributionPayload } : {})
          }
        }
      })

      if (error) throw error

      // Profile is created server-side by the handle_new_user() trigger on auth.users.
      // Don't try to insert from the client — anon role hits RLS and returns 401,
      // which used to surface as a misleading "houve um problema ao configurar o perfil".
      if (data.user?.email_confirmed_at) {
        toast.success('Conta criada com sucesso!')
      } else {
        toast.success('Conta criada! Confirme seu e-mail para entrar.')
      }
    } catch (error) {
      const authError = error as AuthError
      const rawMessage = authError.message || ''
      let friendlyMessage = rawMessage || 'Erro ao criar conta'

      if (/Password should contain at least one character of each/i.test(rawMessage)) {
        friendlyMessage = 'A senha precisa conter pelo menos uma letra minúscula, uma letra maiúscula, um número e um símbolo (ex: !@#$%).'
      } else if (/Password should be at least/i.test(rawMessage)) {
        const min = rawMessage.match(/(\d+)/)?.[1] ?? '8'
        friendlyMessage = `A senha precisa ter pelo menos ${min} caracteres.`
      } else if (/User already registered|already exists|already been registered/i.test(rawMessage)) {
        friendlyMessage = 'Este email já está cadastrado. Tente fazer login.'
      } else if (/Email .* invalid|invalid email/i.test(rawMessage)) {
        friendlyMessage = 'Email inválido. Verifique e tente novamente.'
      }

      toast.error(friendlyMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    try {
      setLoading(true)

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/app`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      })

      if (error) {
        throw error
      }

      // Note: The page will redirect, so we don't need to show a success message here
      // The auth state change will be handled by the onAuthStateChange listener
    } catch (error) {
      const authError = error as AuthError
      toast.error(authError.message || 'Erro ao fazer login com Google')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true);
    try {
      // Use local scope to avoid 403 errors when the server session is already invalid
      // This is common after deployments or when the session has expired
      const { error } = await supabase.auth.signOut({ scope: 'local' });

      // If we get a 403 error, the session is already invalid on the server
      // So we can safely ignore it and just clear the local state
      if (error) {
        // Only throw if it's not a 403/session error
        if (!error.message?.includes('403') && !error.message?.includes('session')) {
          throw error;
        }
      }

      // Always clear local state regardless of server response
      setUser(null);
      setProfile(null);
      setSession(null);
      localStorage.removeItem('hasSeenOnboarding');
      toast.success('Logout realizado com sucesso!');
    } catch (error) {

      // Even on error, clear local state to ensure user is logged out
      setUser(null);
      setProfile(null);
      setSession(null);

      toast.error('Erro ao fazer logout, mas você foi desconectado localmente.');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })
      if (error) throw error
      toast.success('Email de recuperação enviado!')
    } catch (error) {
      const authError = error as AuthError
      toast.error(authError.message || 'Erro ao enviar email de recuperação')
      throw error
    }
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Lida com a atualização do email separadamente, pois ele é do auth.users
      const profileUpdates: Partial<Profile> = { ...updates };

      if (updates.email && updates.email !== user.email) {
        const { error: authError } = await supabase.auth.updateUser({ email: updates.email });
        if (authError) {
          throw authError; // Relança o erro para ser pego pelo catch externo
        }
        // Remove o email de profileUpdates, pois já foi tratado
        delete profileUpdates.email;
      }

      // Atualiza a tabela profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdates)
        .eq('id', user.id);

      if (profileError) {
        throw profileError; // Relança o erro para ser pego pelo catch externo
      }

      // Atualiza o estado local
      setProfile(prev => {
        if (prev) {
          return { ...prev, ...updates };
        }
        return null;
      });

      await refreshProfile();

    } catch (error) {
      toast.error('Erro ao atualizar perfil: ' + (error instanceof Error ? error.message : String(error)));
      throw error; // Relança o erro para ser pego pelo componente chamador (Settings.tsx)
    }
  };

  /**
   * Conclusao DE FATO do onboarding (CTA final). `onboarding_completed_at` e o
   * que distingue esta conclusao real do dado legado, gravado por engano quando
   * o usuario apenas fechava o modal.
   */
  const markOnboardingComplete = async () => {
    if (!user) return

    const completedAt = new Date().toISOString()

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true, onboarding_completed_at: completedAt })
        .eq('id', user.id)

      if (error) throw error

      setProfile(prev =>
        prev ? { ...prev, onboarding_completed: true, onboarding_completed_at: completedAt } : null
      )
      localStorage.setItem('hasSeenOnboarding', 'true')
      trackFunnelEvent('onboarding_completed')
    } catch {
      // Silently fail - onboarding is not critical
      // Still set localStorage so user is not stuck in a loop
      localStorage.setItem('hasSeenOnboarding', 'true')
    }
  }

  /**
   * Abandono: o usuario fechou no X, clicou "Pular" ou saiu pelo backdrop.
   * NAO toca `onboarding_completed` — era exatamente isso que fazia abandono
   * virar conclusao e invalidava qualquer leitura de funil. Registra o passo em
   * que parou e incrementa o contador que governa a reexibicao no Dashboard.
   *
   * O `localStorage` continua sendo escrito mesmo em erro, como na conclusao:
   * ele existe para o usuario nao ficar preso num loop de modal.
   */
  const markOnboardingDismissed = async (step: number, via: OnboardingDismissVia) => {
    if (!user) return

    const dismissedAt = new Date().toISOString()
    const nextCount = (profile?.onboarding_dismiss_count ?? 0) + 1

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          onboarding_step: step,
          onboarding_dismissed_at: dismissedAt,
          onboarding_dismiss_count: nextCount,
        })
        .eq('id', user.id)

      if (error) throw error

      setProfile(prev =>
        prev
          ? {
              ...prev,
              onboarding_step: step,
              onboarding_dismissed_at: dismissedAt,
              onboarding_dismiss_count: nextCount,
            }
          : null
      )
    } catch {
      // Silently fail - onboarding nao e critico.
    } finally {
      localStorage.setItem('hasSeenOnboarding', 'true')
    }

    // O motivo da saida so faz sentido como EVENTO, nao como estado: e o que
    // separa "pulou de proposito" de "fechou sem querer". O wrapper grava no
    // banco (timeline do admin) e espelha no GA4 (funil agregado).
    trackFunnelEvent('onboarding_dismissed', step, { via })
  }

  // Explicitly compute isAdmin, isSchoolAdmin, and isEditor from profile
  const isAdmin = !!profile?.is_admin
  const isSchoolAdmin = !!profile?.is_school_admin
  const isEditor = !!profile?.is_editor

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      loading,
      isAdmin,
      isSchoolAdmin,
      isEditor,
      trialActivating,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
      updateProfile,
      refreshProfile,
      markOnboardingComplete,
      markOnboardingDismissed
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}